import { applyRedistributedWords } from "../utils/gridLineOperationsUtils";
import { useCallback, useEffect } from "react";

/**
 * Hook for grid line confirmation logic
 * Handles confirming, canceling, and managing grid line changes
 */
const useGridLineConfirmation = ({
  // State
  gridLineState,
  modalState,
  tableState,

  // Grid line state
  isDraggingGridLine,
  isGridLineInPreview,
  pendingGridLineChanges,
  originalGridLines,
  dynamicGridLines,
  placingGridLineType,
  draggingGridLine,

  // Table state
  selectedTable,
  tableData,
  currentPage,
  pdfPageDimensions,
  showGridLineConfirmation,

  // Functions
  recalculateCellsFromGridLinesWrapper,
  continueGridLineDragHook,
}) => {
  // Cancel grid line dragging and reset to original state
  const cancelGridLineDrag = useCallback(() => {
    if (originalGridLines) {
      gridLineState.setDynamicGridLines(originalGridLines);
    }
    gridLineState.setIsDraggingGridLine(false);
    gridLineState.setDraggingGridLine(null);
    gridLineState.setGridLineStartPos(null);
    gridLineState.setPlacingGridLineType(null); // Reset placement type

    // Clear originalGridLines immediately since recalculation only happens when no grid lines exist
    gridLineState.setOriginalGridLines(null);
  }, [originalGridLines, gridLineState]);

  // Confirm grid line changes
  const confirmGridLineChanges = useCallback(
    (skipColumnCheck = false, columnName = null) => {
      if (pendingGridLineChanges) {
        // Check if this is a new column case and handle column naming
        const originalColumnCount =
          pendingGridLineChanges.originalTableData.headers.length;
        const newColumnCount =
          pendingGridLineChanges.newTableData.headers.length;
        const isNewColumn = newColumnCount > originalColumnCount;

        let finalTableData;

        if (isNewColumn && columnName) {
          // Apply column name directly to the table data
          const updatedHeaders = [
            ...pendingGridLineChanges.newTableData.headers,
          ];

          // Find the placeholder column (NewColumn_X) and replace it
          let placeholderIndex = -1;
          for (let i = 0; i < updatedHeaders.length; i++) {
            if (updatedHeaders[i].startsWith("NewColumn_")) {
              placeholderIndex = i;
              break;
            }
          }

          if (placeholderIndex >= 0) {
            updatedHeaders[placeholderIndex] = columnName.trim();
          } else {
            // Fallback - update the last column
            updatedHeaders[newColumnCount - 1] = columnName.trim();
          }

          // Update the pending changes with the new header
          finalTableData = {
            ...pendingGridLineChanges.newTableData,
            headers: updatedHeaders,
          };

          // Apply the changes with the updated column name
          tableState.setTableData(finalTableData);
        } else if (isNewColumn && !columnName) {
          // If it's a new column but no name provided, return early
          return;
        } else {
          // Regular case: apply changes as-is
          finalTableData = pendingGridLineChanges.newTableData;
          tableState.setTableData(finalTableData);
        }

        // Cache the modified table data for this table
        const tableId = selectedTable?.table_id || selectedTable?.id;
        if (tableId && finalTableData) {
          tableState.setCachedTableData((prev) => ({
            ...prev,
            [tableId]: {
              headers: [...finalTableData.headers],
              rows: finalTableData.rows.map((row) => [...row]),
            },
          }));
        }

        // This ensures subsequent operations start with the correct baseline
        gridLineState.setDynamicGridLines(pendingGridLineChanges.newGridLines);

        // Update word assignments in selectedTable using the word map from redistribution
        const updatedSelectedTable = applyRedistributedWords(
          selectedTable,
          pendingGridLineChanges.newTableData.__cellWordMap,
          pendingGridLineChanges.newTableData.__currentPage
        );

        tableState.setSelectedTable(updatedSelectedTable);
      }

      // Clean up all state
      modalState.setShowGridLineConfirmation(false);
      gridLineState.setPendingGridLineChanges(null);
      gridLineState.setOriginalGridLines(null);
      gridLineState.setIsGridLineInPreview(false);
      gridLineState.setPlacingGridLineType(null);
      gridLineState.setDraggingGridLine(null);
      gridLineState.setGridLineStartPos(null);
      gridLineState.setPendingNewGridLines(null);
    },
    [
      pendingGridLineChanges,
      modalState,
      tableState,
      selectedTable,
      gridLineState,
    ]
  );

  // Cancel grid line changes and revert
  const cancelGridLineChanges = useCallback(() => {
    if (pendingGridLineChanges && originalGridLines) {
      // Revert to original grid lines
      gridLineState.setDynamicGridLines(originalGridLines);
    }

    // Clean up all state - but delay clearing originalGridLines to prevent immediate recalculation
    modalState.setShowGridLineConfirmation(false);
    gridLineState.setPendingGridLineChanges(null);
    gridLineState.setIsGridLineInPreview(false);
    gridLineState.setPlacingGridLineType(null);
    gridLineState.setDraggingGridLine(null);
    gridLineState.setGridLineStartPos(null);
    gridLineState.setPendingNewGridLines(null);

    // Clear originalGridLines immediately since recalculation only happens when no grid lines exist
    gridLineState.setOriginalGridLines(null);
  }, [pendingGridLineChanges, originalGridLines, gridLineState, modalState]);

  // Stop dragging without confirming - show intermediate modal first
  const stopGridLineDrag = useCallback(() => {
    if (!isDraggingGridLine) {
      return;
    }

    gridLineState.setIsDraggingGridLine(false);
    gridLineState.setIsGridLineInPreview(true); // Enter preview mode

    // Calculate pending changes for both new and moved grid lines
    // Check if we already have pending changes from initial placement
    if (pendingGridLineChanges) {
      // We already have the calculated changes, show intermediate modal
      gridLineState.setShowGridLineMoved(true);
      return;
    }

    if (
      dynamicGridLines?.[currentPage] &&
      selectedTable &&
      tableData &&
      originalGridLines
    ) {
      let newTableData;
      try {
        // Use the stored original grid lines from the pending changes if available
        newTableData = recalculateCellsFromGridLinesWrapper(
          originalGridLines,
          dynamicGridLines
        );
      } catch (error) {
        // Error in recalculation, return early
        return;
      }

      if (newTableData) {
        // Set up pending changes for preview
        gridLineState.setPendingGridLineChanges({
          newTableData,
          newGridLines: dynamicGridLines,
          originalTableData: tableData,
          originalGridLines: originalGridLines,
          affectedPages: [currentPage],
          splitColumnIndex: newTableData.splitColumnIndex, // Store the split column index
          splitRowIndex: newTableData.splitRowIndex, // Store the split row index
        });

        // Show intermediate "Grid line moved" modal first
        gridLineState.setShowGridLineMoved(true);
      }
    }

    // Keep draggingGridLine and dynamicGridLines for preview
    // Don't reset them until user confirms or cancels
  }, [
    isDraggingGridLine,
    dynamicGridLines,
    currentPage,
    selectedTable,
    tableData,
    originalGridLines,
    pendingGridLineChanges,
    gridLineState,
    recalculateCellsFromGridLinesWrapper,
  ]);

  // Cancel the grid line changes and revert (after dragging)
  const cancelGridLinePreview = useCallback(() => {
    if (!isGridLineInPreview) return;

    gridLineState.setIsGridLineInPreview(false);
    gridLineState.setPersistentGridLineTooltip({ show: false });

    // Revert to original grid lines
    if (originalGridLines) {
      gridLineState.setDynamicGridLines(originalGridLines);
    }

    // Clear pending changes
    gridLineState.setPendingGridLineChanges(null);

    // Reset all drag-related state
    gridLineState.setIsDraggingGridLine(false);
    gridLineState.setDraggingGridLine(null);
    gridLineState.setGridLineStartPos(null);
    gridLineState.setPlacingGridLineType(null);
    gridLineState.setOriginalGridLines(null);
  }, [isGridLineInPreview, originalGridLines, gridLineState]);

  // Local confirmGridLinePlacement wrapper for modal logic
  const confirmGridLinePlacement = useCallback(() => {
    if (!isGridLineInPreview) {
      return;
    }

    gridLineState.setIsGridLineInPreview(false);
    gridLineState.setPersistentGridLineTooltip({ show: false });

    // Require pending changes to proceed
    if (!pendingGridLineChanges) {
      // No pending changes, return early
      return;
    }

    // Always show the GridLineConfirmationModal - it will handle both regular changes and new columns with name input
    modalState.setShowGridLineConfirmation(true);
  }, [isGridLineInPreview, pendingGridLineChanges, gridLineState, modalState]);

  // Handle intermediate modal confirmation - proceed to full modal
  const confirmGridLineMoved = useCallback(() => {
    // Close intermediate modal
    gridLineState.setShowGridLineMoved(false);

    // Show the full confirmation modal
    modalState.setShowGridLineConfirmation(true);
  }, [gridLineState, modalState]);

  // Handle intermediate modal cancellation - revert changes
  const cancelGridLineMoved = useCallback(() => {
    // Close intermediate modal
    gridLineState.setShowGridLineMoved(false);

    // Revert to original grid lines
    if (originalGridLines) {
      gridLineState.setDynamicGridLines(originalGridLines);
    }

    // Clear all states - but delay clearing originalGridLines to prevent immediate recalculation
    gridLineState.setPendingGridLineChanges(null);
    gridLineState.setIsGridLineInPreview(false);
    gridLineState.setDraggingGridLine(null);
    gridLineState.setGridLineStartPos(null);
    gridLineState.setPersistentGridLineTooltip({ show: false });

    // Clear originalGridLines immediately since recalculation only happens when no grid lines exist
    gridLineState.setOriginalGridLines(null);
  }, [gridLineState, originalGridLines]);

  // Global mouse event listeners for grid line dragging
  useEffect(() => {
    if (isDraggingGridLine) {
      document.addEventListener("mousemove", continueGridLineDragHook);
      document.addEventListener("mouseup", stopGridLineDrag);
      document.body.style.cursor =
        draggingGridLine?.type === "horizontal" ? "ns-resize" : "ew-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", continueGridLineDragHook);
        document.removeEventListener("mouseup", stopGridLineDrag);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [
    isDraggingGridLine,
    continueGridLineDragHook,
    stopGridLineDrag,
    draggingGridLine,
  ]);

  return {
    cancelGridLineDrag,
    confirmGridLineChanges,
    cancelGridLineChanges,
    stopGridLineDrag,
    cancelGridLinePreview,
    confirmGridLinePlacement,
    confirmGridLineMoved,
    cancelGridLineMoved,
  };
};

export default useGridLineConfirmation;
